import { useState } from 'react';
import { Filter, X, ChevronDown } from 'lucide-react';

export interface FilterValues {
  platform: string[];
  dateRange: string;
  minFollowers: number | null;
  maxFollowers: number | null;
  category: string;
  minPrice: number | null;
  maxPrice: number | null;
}

interface MarketFiltersProps {
  filters: FilterValues;
  onFiltersChange: (filters: FilterValues) => void;
}

const PLATFORMS = ['Instagram', 'TikTok', 'YouTube'];
const DATE_RANGES = [
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
  { value: 'all', label: 'Tout' },
];

export default function MarketFilters({ filters, onFiltersChange }: MarketFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handlePlatformToggle = (platform: string) => {
    const newPlatforms = filters.platform.includes(platform)
      ? filters.platform.filter((p) => p !== platform)
      : [...filters.platform, platform];
    onFiltersChange({ ...filters, platform: newPlatforms });
  };

  const handleReset = () => {
    onFiltersChange({
      platform: [],
      dateRange: '30',
      minFollowers: null,
      maxFollowers: null,
      category: '',
      minPrice: null,
      maxPrice: null,
    });
  };

  const hasActiveFilters =
    filters.platform.length > 0 ||
    filters.minFollowers !== null ||
    filters.maxFollowers !== null ||
    filters.minPrice !== null ||
    filters.maxPrice !== null ||
    filters.category !== '' ||
    filters.dateRange !== '30';

  const activeFiltersCount = [
    filters.platform.length > 0,
    filters.minFollowers !== null || filters.maxFollowers !== null,
    filters.minPrice !== null || filters.maxPrice !== null,
    filters.category !== '',
    filters.dateRange !== '30',
  ].filter(Boolean).length;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border font-medium transition-all ${
          hasActiveFilters
            ? 'bg-white/[0.10] border-white/20 text-white'
            : 'bg-white/[0.08] border-white/[0.08] text-white/60 hover:bg-white/[0.14]'
        }`}
      >
        <Filter className="w-4 h-4" />
        <span>Filtres</span>
        {hasActiveFilters && (
          <span className="bg-white text-black text-xs font-bold px-2 py-0.5 rounded-full">
            {activeFiltersCount}
          </span>
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full mt-2 right-0 z-50 bg-[#141414] rounded-2xl border border-white/[0.08] shadow-2xl p-6 w-[500px]">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">Filtres avancés</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/40 hover:text-white/70 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2.5">
                  Plateformes
                </label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((platform) => (
                    <button
                      key={platform}
                      onClick={() => handlePlatformToggle(platform)}
                      className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                        filters.platform.includes(platform)
                          ? 'bg-white/[0.12] border-white/30 text-white'
                          : 'bg-white/[0.04] border-white/[0.08] text-white/50 hover:bg-white/[0.08] hover:text-white/70'
                      }`}
                    >
                      {platform}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2.5">
                  Période
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {DATE_RANGES.map((range) => (
                    <button
                      key={range.value}
                      onClick={() => onFiltersChange({ ...filters, dateRange: range.value })}
                      className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
                        filters.dateRange === range.value
                          ? 'bg-white/[0.12] border-white/30 text-white'
                          : 'bg-white/[0.04] border-white/[0.08] text-white/50 hover:bg-white/[0.08] hover:text-white/70'
                      }`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2.5">
                  Nombre de followers
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5">Min</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={filters.minFollowers || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        onFiltersChange({ ...filters, minFollowers: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5">Max</label>
                    <input
                      type="number"
                      placeholder="∞"
                      value={filters.maxFollowers || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        onFiltersChange({ ...filters, maxFollowers: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { label: '1K-10K', min: 1000, max: 10000 },
                    { label: '10K-50K', min: 10000, max: 50000 },
                    { label: '50K-100K', min: 50000, max: 100000 },
                    { label: '100K+', min: 100000, max: null },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          minFollowers: preset.min,
                          maxFollowers: preset.max,
                        })
                      }
                      className="px-3 py-1.5 text-xs font-medium bg-white/[0.06] hover:bg-white/[0.10] text-white/50 hover:text-white/80 rounded-lg transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2.5">
                  Prix estimé (€)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5">Min</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={filters.minPrice || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        onFiltersChange({ ...filters, minPrice: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5">Max</label>
                    <input
                      type="number"
                      placeholder="∞"
                      value={filters.maxPrice || ''}
                      onChange={(e) => {
                        const value = e.target.value ? parseInt(e.target.value) : null;
                        onFiltersChange({ ...filters, maxPrice: value });
                      }}
                      className="w-full px-3 py-2 bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl focus:ring-1 focus:ring-white/20 outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6 pt-5 border-t border-white/[0.06]">
              <button
                onClick={handleReset}
                disabled={!hasActiveFilters}
                className="flex-1 px-4 py-2.5 bg-white/[0.06] hover:bg-white/[0.10] disabled:opacity-40 disabled:cursor-not-allowed text-white/60 font-medium rounded-xl transition-colors"
              >
                Réinitialiser
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="flex-1 px-4 py-2.5 bg-white text-black hover:bg-white/90 font-semibold rounded-xl transition-colors"
              >
                Appliquer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
